import { apiClient } from '@borradh-workspace/api-client';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { queryKeys } from '@/lib/query-keys';

import type { AppVersionCheck } from '../../types';

/**
 * Read the marketing version of the running binary.
 *
 * `App.getInfo()` is native-only — on web it rejects, so the whole check is
 * skipped there. An open web tab CAN run an outdated bundle; that is handled
 * by `src/lib/web-build-refresh.ts`, which reloads it onto the live deploy.
 */
const getNativeVersion = async (): Promise<{
  platform: 'ios' | 'android';
  version: string;
} | null> => {
  if (!Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return null;

  try {
    const info = await App.getInfo();
    return { platform, version: info.version };
  } catch {
    // Plugin unavailable (simulator without the native layer). Not knowing the
    // version is not grounds for blocking anyone.
    return null;
  }
};

export const useCheckAppVersion = () => {
  const isNative = Capacitor.isNativePlatform();
  const query = useQuery({
    queryKey: queryKeys.appVersion.check(),
    queryFn: async (): Promise<AppVersionCheck | null> => {
      const native = await getNativeVersion();
      if (!native) return null;

      return apiClient.get<AppVersionCheck>(
        `app-version/check?platform=${native.platform}&version=${encodeURIComponent(native.version)}`
      );
    },
    // The answer can change while this process sits backgrounded during a
    // release. The explicit app-state listener below owns resume checks.
    staleTime: Number.POSITIVE_INFINITY,
    // Never retry, and never surface a failure as a block. If the API is
    // unreachable, the user gets the app — see the fail-open note on the
    // backend service. Blocking on a network blip would be the worst possible
    // failure mode for a gate whose only escape hatch is the App Store.
    retry: false,
    enabled: isNative,
  });

  const { refetch } = query;
  useEffect(() => {
    if (!isNative) return;

    let disposed = false;
    let removeListener: (() => Promise<void>) | undefined;

    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void refetch();
    }).then((handle) => {
      if (disposed) {
        void handle.remove();
      } else {
        removeListener = () => handle.remove();
      }
    });

    return () => {
      disposed = true;
      void removeListener?.();
    };
  }, [isNative, refetch]);

  return {
    check: query.data ?? null,
    // Only the first check may hide the router. A resume refetch must leave
    // file inputs and dialogs mounted while the native picker returns.
    isChecking: query.isLoading,
    isRechecking: query.isFetching && !query.isLoading,
  };
};
